import React, { useState, useEffect } from 'react';
import { FaUserClock, FaTasks, FaChartLine, FaLinkedin, FaProjectDiagram } from 'react-icons/fa';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import CustomerWindow from '../crm/customerWindow';
import './styles/dashboardMain.css';

function DashboardMain() {
  const [followUps, setFollowUps] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerWindow, setShowCustomerWindow] = useState(false);
  const [deadlines, setDeadlines] = useState([]);
  
  useEffect(() => {
    // Lyt efter ændringer i customers collection
    const unsubscribe = onSnapshot(
      collection(db, 'customers'),
      async (snapshot) => {
        const now = new Date();
        const customersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter og sortér leads der skal følges op på
        const followUpsData = customersData
          .filter(customer => {
            // Kun vis kundens egne leads og ikke vundne
            if (customer.accountOwner !== auth.currentUser.uid || customer.status === 'won') {
              return false;
            }

            // Check sidste opdatering
            if (!customer.updates || customer.updates.length === 0) return false;

            const lastUpdate = customer.updates.reduce((latest, current) => 
              new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
            );

            const daysSinceUpdate = Math.floor(
              (now - new Date(lastUpdate.timestamp)) / (1000 * 60 * 60 * 24)
            );

            return daysSinceUpdate > 7;
          })
          .sort((a, b) => {
            // Sortér efter dage siden sidste opdatering (faldende)
            const getLastUpdateDays = (customer) => {
              const lastUpdate = customer.updates.reduce((latest, current) => 
                new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
              );
              return Math.floor((now - new Date(lastUpdate.timestamp)) / (1000 * 60 * 60 * 24));
            };

            return getLastUpdateDays(b) - getLastUpdateDays(a);
          });

        setFollowUps(followUpsData);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchDeadlines = async () => {
      try {
        // Hent projekter hvor brugeren er ansvarlig
        const projectsQuery = query(
          collection(db, 'projects'),
          where('responsible', '==', auth.currentUser.uid),
          where('status', '!=', 'done')
        );

        // Hent opgaver hvor brugeren er ansvarlig
        const tasksQuery = query(
          collection(db, 'tasks'),
          where('responsible', '==', auth.currentUser.uid),
          where('completed', '==', false)
        );

        // Lyt til ændringer i projekter
        const unsubscribeProjects = onSnapshot(projectsQuery, (projectsSnapshot) => {
          const projectDeadlines = projectsSnapshot.docs
            .filter(doc => doc.data().deadline) // Kun projekter med deadline
            .map(doc => ({
              id: doc.id,
              type: 'project',
              name: doc.data().name,
              deadline: doc.data().deadline,
              description: doc.data().description || 'Ingen beskrivelse'
            }));

          // Lyt til ændringer i opgaver
          const unsubscribeTasks = onSnapshot(tasksQuery, (tasksSnapshot) => {
            const taskDeadlines = tasksSnapshot.docs
              .filter(doc => doc.data().deadline) // Kun opgaver med deadline
              .map(doc => ({
                id: doc.id,
                type: 'task',
                name: doc.data().name,
                deadline: doc.data().deadline,
                description: `Opgave${doc.data().projectId ? ' i projekt' : ''}`
              }));

            // Kombiner og sortér alle deadlines
            const allDeadlines = [...projectDeadlines, ...taskDeadlines]
              .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
              .filter(item => {
                const deadline = new Date(item.deadline);
                const today = new Date();
                return deadline >= today;
              })
              .slice(0, 5); // Vis kun de 5 næste deadlines

            setDeadlines(allDeadlines);
          });

          return () => unsubscribeTasks();
        });

        return () => unsubscribeProjects();
      } catch (error) {
        console.error('Fejl ved hentning af deadlines:', error);
      }
    };

    if (auth.currentUser) {
      fetchDeadlines();
    }
  }, []);

  const formatCurrency = (amount) => {
    if (!amount) return '0 kr';
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setShowCustomerWindow(true);
  };

  const handleCloseCustomerWindow = () => {
    setShowCustomerWindow(false);
    setSelectedCustomer(null);
  };

  const getDeadlineStatus = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const daysUntil = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil <= 3) {
      return 'urgent';
    }
    return '';
  };

  return (
    <div className="dashboard-container">
      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <FaUserClock />
          </div>
          <div className="stat-content">
            <h3>Kunder der skal opfølges</h3>
            <p className="stat-value">12</p>
            <p className="stat-change positive">+2 siden i går</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <FaTasks />
          </div>
          <div className="stat-content">
            <h3>Opgaver</h3>
            <p className="stat-value">8</p>
            <p className="stat-change negative">3 overskredet</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <FaChartLine />
          </div>
          <div className="stat-content">
            <h3>Pipelineværdi</h3>
            <p className="stat-value">2.4M DKK</p>
            <p className="stat-change positive">+300K denne måned</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <FaLinkedin />
          </div>
          <div className="stat-content">
            <h3>LinkedIn DM's</h3>
            <p className="stat-value">24</p>
            <p className="stat-change">5 nye i dag</p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        <div className="dashboard-card deadlines">
          <h2>Kommende deadlines</h2>
          <div className="deadline-list">
            {deadlines.length > 0 ? (
              deadlines.map(item => (
                <div key={`${item.type}-${item.id}`} className="deadline-item">
                  <div className={`deadline-date ${getDeadlineStatus(item.deadline)}`}>
                    <span className="day">
                      {new Date(item.deadline).getDate()}
                    </span>
                    <span className="month">
                      {new Date(item.deadline).toLocaleString('da-DK', { month: 'short' })}
                    </span>
                  </div>
                  <div className="deadline-content">
                    <div className="deadline-header">
                      <h4>{item.name}</h4>
                      <span className={`deadline-type ${item.type}`}>
                        <FaProjectDiagram />
                        {item.type === 'project' ? 'Projekt' : 'Opgave'}
                      </span>
                    </div>
                    <p>{item.description}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-deadlines">
                Ingen kommende deadlines
              </div>
            )}
          </div>
        </div>

        {/* Opdateret Skal opfølges sektion */}
        <div className="dashboard-card followups">
          <h2>Skal opfølges</h2>
          <div className="followup-list">
            {followUps.map(customer => {
              const lastUpdate = customer.updates.reduce((latest, current) => 
                new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
              );
              
              const daysSinceUpdate = Math.floor(
                (new Date() - new Date(lastUpdate.timestamp)) / (1000 * 60 * 60 * 24)
              );

              return (
                <div 
                  key={customer.id} 
                  className={`followup-item ${daysSinceUpdate > 14 ? 'critical' : 'warning'}`}
                  onClick={() => handleCustomerClick(customer)}
                >
                  <div className="followup-status">
                    <span className="status-dot"></span>
                    {daysSinceUpdate} dage
                  </div>
                  <div className="followup-content">
                    <h4>{customer.name}</h4>
                    <p>Sidste kontakt: {new Date(lastUpdate.timestamp).toLocaleDateString('da-DK')}</p>
                  </div>
                  <div className="followup-value">
                    {formatCurrency(customer.estimatedValue)}
                  </div>
                </div>
              );
            })}
            {followUps.length === 0 && (
              <div className="no-followups">
                Ingen leads der skal følges op på lige nu
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tilføj CustomerWindow */}
      {showCustomerWindow && selectedCustomer && (
        <CustomerWindow
          customer={selectedCustomer}
          onClose={handleCloseCustomerWindow}
          onUpdate={() => {
            // Opdateringen håndteres automatisk via onSnapshot
          }}
        />
      )}
    </div>
  );
}

export default DashboardMain; 